/**
 * Роль в приложении — только «администратор» или «сотрудник». Должность (оператор, мастер, начальник…)
 * — отдельно: это иерархия компании (packages/shared/src/dto.ts → PositionDto), прав она не даёт,
 * по ней, например, замечания из проверок уходят руководителю.
 * Сервер проверяет права сам; приложение по этой же таблице только прячет лишние кнопки.
 */
export const ROLES = ['admin', 'employee'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Администратор',
  employee: 'Сотрудник',
};

export const PERMISSIONS = {
  'panel.view': 'Открывать панели оборудования своих участков и управлять ими; проходить проверки оборудования',
  'panel.manage': 'Добавлять и настраивать панели и участки',
  'panel.log': 'Смотреть журнал: кто подключался и что нажимал',
  'session.terminate': 'Обрывать чужие подключения к панелям',
  'areas.all': 'Видеть оборудование всех участков, а не только своих',
  'staff.manage': 'Добавлять сотрудников, должности и участки',
  'equipment.manage': 'Заводить оборудование: списки действий, документы, фото и видео',
} as const;
export type Permission = keyof typeof PERMISSIONS;

/**
 * Что видит сотрудник, задают участки (Ещё → Сотрудники → участки).
 * Открыть панель = управлять ею. Только просмотр — настройка конкретной панели («Разрешить управление»).
 */
const MATRIX: Record<Role, readonly Permission[]> = {
  admin: ['panel.view', 'panel.manage', 'panel.log', 'session.terminate', 'areas.all', 'staff.manage', 'equipment.manage'],
  employee: ['panel.view'],
};

export function isRole(v: unknown): v is Role {
  return typeof v === 'string' && (ROLES as readonly string[]).includes(v);
}

export function can(role: Role | null | undefined, perm: Permission): boolean {
  return !!role && MATRIX[role].includes(perm);
}

export function permissionsOf(role: Role): Permission[] {
  return [...MATRIX[role]];
}
